import { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Download, CheckCircle, ArrowLeft } from 'lucide-react';
import { usePWAInstall } from '@/hooks/usePWAInstall';
import { Link } from 'react-router-dom';
import { useTransition } from '@/hooks/useTransition';

export default function Install() {
  const { installable, installing, installed, install } = usePWAInstall();
  const { navigateWithTransition } = useTransition();
  const backButtonRef = useRef<HTMLAnchorElement>(null);

  const handleBack = () => {
    const rect = backButtonRef.current?.getBoundingClientRect();
    navigateWithTransition('/', rect);
  };

  const handleInstall = async () => {
    await install();
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Psychedelic moving background */}
      <div className="absolute inset-0 -z-10">
        {/* Base gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-rose-200 via-amber-100 via-emerald-100 to-sky-200 dark:from-rose-950 dark:via-violet-950 dark:to-sky-950" />
        
        {/* Floating color blobs */}
        <div className="absolute inset-0 overflow-hidden">
          <div 
            className="absolute w-[600px] h-[600px] rounded-full opacity-40 dark:opacity-20 blur-3xl animate-blob-1"
            style={{ background: 'radial-gradient(circle, #f472b6 0%, transparent 70%)', top: '-10%', left: '-10%' }}
          />
          <div 
            className="absolute w-[500px] h-[500px] rounded-full opacity-40 dark:opacity-20 blur-3xl animate-blob-2"
            style={{ background: 'radial-gradient(circle, #fbbf24 0%, transparent 70%)', top: '20%', right: '-15%' }}
          />
          <div 
            className="absolute w-[700px] h-[700px] rounded-full opacity-30 dark:opacity-15 blur-3xl animate-blob-3"
            style={{ background: 'radial-gradient(circle, #34d399 0%, transparent 70%)', bottom: '-20%', left: '20%' }}
          />
          <div 
            className="absolute w-[400px] h-[400px] rounded-full opacity-40 dark:opacity-20 blur-3xl animate-blob-4"
            style={{ background: 'radial-gradient(circle, #818cf8 0%, transparent 70%)', top: '50%', left: '-5%' }}
          />
          <div 
            className="absolute w-[550px] h-[550px] rounded-full opacity-35 dark:opacity-18 blur-3xl animate-blob-5"
            style={{ background: 'radial-gradient(circle, #f97316 0%, transparent 70%)', bottom: '10%', right: '-10%' }}
          />
          <div 
            className="absolute w-[450px] h-[450px] rounded-full opacity-30 dark:opacity-15 blur-3xl animate-blob-6"
            style={{ background: 'radial-gradient(circle, #06b6d4 0%, transparent 70%)', top: '-5%', right: '30%' }}
          />
        </div>
      </div>

      {/* Header - minimal */}
      <header className="sticky top-0 z-50 safe-top-mobile">
        <div className="max-w-xl mx-auto px-5 py-4">
          <Link 
            ref={backButtonRef}
            to="/" 
            onClick={(e) => {
              e.preventDefault();
              handleBack();
            }}
            className="p-2 -ml-2 rounded-full inline-flex text-foreground/60 hover:text-foreground hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="relative z-10 min-h-[calc(100vh-80px)] flex flex-col items-center justify-center px-6 pb-20">
        <div className="max-w-md w-full space-y-12 text-center">
          
          {/* Already installed state */}
          {installed && (
            <div className="space-y-6">
              <div className="flex justify-center">
                <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <CheckCircle className="h-10 w-10 text-emerald-600 dark:text-emerald-400" />
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-xl text-foreground">already installed</p>
                <p className="text-muted-foreground">find espy on your home screen</p>
              </div>
            </div>
          )}

          {/* Install button available */}
          {installable && !installed && (
            <div className="space-y-8">
              <div className="space-y-2">
                <p className="text-2xl text-foreground">install espy</p>
                <p className="text-muted-foreground">one tap</p>
              </div>
              
              <Button 
                size="lg" 
                onClick={handleInstall}
                disabled={installing}
                className="px-8 py-6 text-lg rounded-full bg-gradient-to-r from-rose-400 via-amber-400 to-emerald-400 hover:from-rose-500 hover:via-amber-500 hover:to-emerald-500 text-white shadow-xl shadow-amber-300/40 dark:shadow-amber-900/40 hover:scale-105 active:scale-95 transition-all"
              >
                <Download className="h-5 w-5 mr-2" />
                {installing ? 'installing...' : 'install'}
              </Button>
            </div>
          )}

          {/* Manual instructions */}
          {!installed && (
            <div className="space-y-8">
              {!installable && (
                <p className="text-2xl text-foreground">add espy to your home screen</p>
              )}
              
              {installable && (
                <div className="pt-8 border-t border-foreground/10">
                  <p className="text-sm text-muted-foreground mb-6">or manually</p>
                </div>
              )}

              <div className="space-y-6 text-left">
                {/* iOS Safari */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground/80">safari on iphone/ipad</p>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    tap share <span className="inline-block px-1.5 py-0.5 bg-foreground/10 rounded text-xs font-mono">[ ]</span> then "add to home screen"
                  </p>
                </div>

                {/* Android Chrome */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground/80">chrome on android</p>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    tap menu <span className="inline-block px-1.5 py-0.5 bg-foreground/10 rounded text-xs font-mono">...</span> then "add to home screen"
                  </p>
                </div>

                {/* Desktop */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground/80">desktop browser</p>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    look for install icon in address bar or browser menu
                  </p>
                </div>
              </div>
            </div>
          )}

        </div>
      </main>
    </div>
  );
}
